import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Radio, GraduationCap, Radar } from "lucide-react";
import { getPartners } from "@/lib/api";

const ABOUT_IMG =
  "https://images.unsplash.com/photo-1662242723207-13ad21d3816f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwzfHxwaWxvdCUyMGNvY2twaXQlMjBnbG93aW5nJTIwbmlnaHR8ZW58MHx8fHwxNzgzNzgyOTQzfDA&ixlib=rb-4.1.0&q=70&w=1280&auto=format"; // shown in a half-width column, so 1280px is enough even on sharp screens (3 MB → ~70 KB)

const PILLARS = [
  { icon: Radar, title: "Aviate", text: "Fly realistic routes across the IVAO & VATSIM networks with live ATC." },
  { icon: GraduationCap, title: "Educate", text: "Learn procedures, phraseology and airmanship from veteran virtual pilots." },
  { icon: Radio, title: "Communicate", text: "Join a global cockpit — a tight-knit community of Indian aviation lovers." },
];

const PARTNER_LOGOS = {
  IVAO: "/logos/ivao.svg",
  VATSIM: "/logos/vatsim.png",
  IndiaVACC: "/logos/indiavacc.svg",
};

export const About = () => {
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    getPartners().then(setPartners).catch(() => {});
  }, []);

  return (
    <section id="about" data-testid="about-section" className="relative py-24 lg:py-32">
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 lg:grid-cols-2 lg:gap-20 lg:px-10">
        <motion.div
          initial={{ opacity: 0, x: -30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="relative"
        >
          <div className="overflow-hidden rounded-2xl">
            <img
              src={ABOUT_IMG}
              loading="lazy" // below the fold: only download when the visitor scrolls near it
              decoding="async" // decode off the main thread so scrolling stays smooth
              alt="Glowing airliner cockpit at night"
              className="h-[420px] w-full object-cover"
            />
          </div>
          <div className="glass absolute -bottom-6 -right-4 rounded-xl px-6 py-4 lg:right-6">
            <p className="font-mono-data text-2xl font-bold text-[var(--aiv-accent)]">15+ yrs</p>
            <p className="text-xs text-[var(--aiv-text-2)]">of continuous operations</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: 30 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="text-overline text-[#8A1538]">Who we are</p>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-[var(--aiv-text)] lg:text-4xl">
            Aviate. Educate. Communicate.
          </h2>
          <p className="mt-5 text-base leading-relaxed text-[var(--aiv-text-2)]">
            Air India Virtual is a group of flight simulator enthusiasts using
            simulators like MSFS, P3D, FSX and X-Plane. Together with our partners
            we recreate the pride of Indian aviation, one virtual flight at a time.
          </p>

          <div className="mt-8 space-y-5">
            {PILLARS.map((p) => (
              <div key={p.title} className="flex gap-4">
                <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full border border-[var(--aiv-accent)]/30 bg-[var(--aiv-accent)]/5">
                  <p.icon className="h-5 w-5 text-[var(--aiv-accent)]" />
                </span>
                <div>
                  <h3 className="text-lg font-semibold text-[var(--aiv-text)]">{p.title}</h3>
                  <p className="text-sm text-[var(--aiv-text-2)]">{p.text}</p>
                </div>
              </div>
            ))}
          </div>

          {partners.length > 0 && (
            <div className="mt-10">
              <p className="text-overline mb-4 text-[var(--aiv-text-2)]">Our Partners</p>
              <div className="flex flex-wrap items-center gap-x-8 gap-y-5" data-testid="partners-list">
                {partners.map((p) => {
                  const logo = PARTNER_LOGOS[p.name];
                  return (
                    <a
                      key={p.name}
                      href={p.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`partner-logo-${p.name.toLowerCase()}`}
                      aria-label={p.name}
                      title={p.name}
                      className="opacity-80 transition-opacity duration-200 hover:opacity-100"
                    >
                      {logo ? (
                        <img src={logo} alt={`${p.name} logo`} className="h-7 w-auto object-contain md:h-8" />
                      ) : (
                        <span className="text-sm font-semibold text-[var(--aiv-text)]">{p.name}</span>
                      )}
                    </a>
                  );
                })}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};
